<script>
  import { router } from '@inertiajs/svelte'
  
  let {
    user = {},
    flash = {},
    work_email = null,
    code_length = 6,
    code_ttl_minutes = 10
  } = $props()

  // 🔴 회사 이메일은 `work_email` prop 으로만 온다 — `current_user_data` 에는 그 키가
  // 없어서 `user.work_email` 은 항상 undefined 다. 그걸 조건으로 쓰면 이미 등록된
  // 사용자에게도 "이메일 입력 폼"만 보이고, 재발송·인증번호 입력이 통째로 가려진다.
  const registeredEmail = $derived(work_email || user.work_email || '')

  // svelte-ignore state_referenced_locally -- intentional: form field initialized from prop
  let workEmail = $state(work_email || user.work_email || '')
  let isResending = $state(false)
  let isUpdating = $state(false)
  let isVerifyingCode = $state(false)
  let showEmailForm = $state(false)
  let code = $state('')

  // 인증번호는 숫자만 받는다 — 메일에서 복사할 때 딸려오는 공백·하이픈을 여기서 털어낸다.
  // 🔴 `bind:value` 와 함께 쓴다. 단방향 `value={code}` 로 두면 걸러진 문자가 state 를
  // 바꾸지 못해 재렌더가 일어나지 않고, 입력칸에는 그 문자가 그대로 남는다.
  function normalizeCode() {
    code = code.replace(/\D/g, '').slice(0, code_length)
  }

  function submitCode(e) {
    e.preventDefault()
    if (code.length !== code_length) return
    isVerifyingCode = true
    router.post('/verification/verify_code', { code }, {
      onFinish: () => {
        isVerifyingCode = false
        code = ''
      }
    })
  }
  
  function resendVerification() {
    isResending = true
    router.post('/verification/resend', {}, {
      onFinish: () => {
        isResending = false
      }
    })
  }
  
  function updateWorkEmail(e) {
    e.preventDefault()
    isUpdating = true
    router.post('/verification/update_email', {
      work_email: workEmail
    }, {
      onFinish: () => {
        isUpdating = false
        showEmailForm = false
      }
    })
  }
</script>

<div class="min-h-screen bg-gray-50 dark:bg-dark-primary flex items-center justify-center px-4 py-10">
  <div class="max-w-md w-full">
    <!-- Logo -->
    <div class="text-center mb-8">
      <a href="/" class="inline-block">
        <h1 class="text-display text-primary mb-2">AX Hub</h1>
      </a>
    </div>
    
    <!-- Verification Card -->
    <div class="card card-bordered p-8 shadow-lg">
      <!-- Flash Messages -->
      {#if flash.alert}
        <div class="mb-6 p-4 bg-error-light dark:bg-error/20 border border-error/30 rounded-xl text-error text-body2">
          {flash.alert}
        </div>
      {/if}
      
      {#if flash.notice}
        <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
          {flash.notice}
        </div>
      {/if}
      
      <!-- Icon -->
      <div class="flex justify-center mb-6">
        <div class="w-20 h-20 bg-primary-50 dark:bg-primary/20 rounded-full flex items-center justify-center">
          <svg class="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>
      
      <h2 class="text-h2 font-semibold text-content text-center mb-2">회사 이름을 표시하려면</h2>
      <p class="text-body2 text-content-secondary text-center mb-6">
        {#if registeredEmail}
          <span class="text-primary font-medium">{registeredEmail}</span>으로 인증 메일을 발송했습니다.
        {:else}
          프로필과 글에 회사 이름을 달려면<br />
          회사 이메일로 소속을 인증해주세요.
        {/if}
      </p>
      
      {#if registeredEmail && !showEmailForm}
        <!-- Resend Section -->
        <div class="space-y-4">
          <div class="bg-surface-tertiary border border-default rounded-xl p-4 text-body2 text-gray-700 dark:text-dark-text-medium">
            <p class="mb-2 font-medium">📧 메일함을 확인해주세요</p>
            <ul class="list-disc list-inside space-y-1 text-caption text-content-tertiary break-keep">
              <li>스팸 폴더도 확인해주세요</li>
              <li>인증 링크는 24시간 동안 유효합니다</li>
              <li>인증번호는 {code_ttl_minutes}분 동안 유효합니다</li>
            </ul>
          </div>

          <!-- 인증번호 입력 — 사내망·VPN 처럼 링크가 열리지 않는 환경용 경로 -->
          <form onsubmit={submitCode} class="space-y-3">
            <label for="verification_code" class="block text-label text-gray-700 dark:text-dark-text-medium">
              메일로 받은 인증번호
            </label>
            <!-- maxlength 를 걸지 않는다 — "123 456" 처럼 공백이 섞인 붙여넣기에서
                 공백이 길이를 잡아먹어 마지막 자리가 잘린다. 자르는 일은 normalizeCode 가 한다. -->
            <input
              type="text"
              id="verification_code"
              bind:value={code}
              oninput={normalizeCode}
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder={'0'.repeat(code_length)}
              class="input text-center text-h2 font-semibold tracking-[0.4em]"
            />
            <p class="text-caption text-content-tertiary break-keep">
              사내망이나 VPN 때문에 링크가 열리지 않는다면,<br />
              메일에 함께 적힌 {code_length}자리 인증번호를 입력해주세요.
            </p>

            <button
              type="submit"
              disabled={isVerifyingCode || code.length !== code_length}
              class="btn btn-primary btn-lg w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {#if isVerifyingCode}
                <span class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  확인 중...
                </span>
              {:else}
                인증번호로 인증하기
              {/if}
            </button>
          </form>

          <!-- Divider: 또는 -->
          <div class="relative my-2">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-default/60"></div>
            </div>
            <div class="relative flex justify-center">
              <span class="px-4 bg-surface text-caption text-content-tertiary">또는</span>
            </div>
          </div>
          
          <button
            onclick={resendVerification}
            disabled={isResending}
            class="btn btn-secondary btn-lg w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {#if isResending}
              <span class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                발송 중...
              </span>
            {:else}
              인증 메일 다시 보내기
            {/if}
          </button>
          
          <button
            onclick={() => showEmailForm = true}
            class="btn btn-ghost btn-lg w-full"
          >
            다른 이메일로 변경하기
          </button>
        </div>
      {:else}
        <!-- Email Form -->
        <form onsubmit={updateWorkEmail} class="space-y-4">
          <div>
            <label for="work_email" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">회사 이메일</label>
            <input
              type="email"
              id="work_email"
              bind:value={workEmail}
              required
              class="input"
              placeholder="you@company.com"
            />
            <p class="mt-2 text-caption text-content-tertiary">
              회사 도메인 이메일을 입력해주세요 (예: @samsung.com, @kakao.com)
            </p>
          </div>
          
          <button
            type="submit"
            disabled={isUpdating || !workEmail}
            class="btn btn-primary btn-lg w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {#if isUpdating}
              <span class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                처리 중...
              </span>
            {:else}
              인증 메일 발송
            {/if}
          </button>
          
          {#if registeredEmail}
            <button
              type="button"
              onclick={() => showEmailForm = false}
              class="btn btn-secondary btn-lg w-full"
            >
              취소
            </button>
          {/if}
        </form>
      {/if}
      
      <!-- Skip/Later Option -->
      <div class="mt-6 pt-6 border-t border-default">
        <a
          href="/boards"
          class="block text-center text-body2 text-content-tertiary hover:text-primary transition-colors"
        >
          건너뛰고 둘러보기 →
        </a>
        <p class="mt-2 text-caption text-gray-400 dark:text-dark-text-disabled text-center">
          공개 게시판은 인증 없이도 이용할 수 있습니다
        </p>
      </div>
    </div>
    
    <!-- Logout -->
    <p class="mt-6 text-center">
      <a href="/logout" data-method="delete" class="text-gray-500 text-body2 hover:text-gray-700 dark:hover:text-dark-text-low transition-colors">
        다른 계정으로 로그인
      </a>
    </p>
  </div>
</div>
